import { useMemo, useState } from 'react';
import { Box, Chip, IconButton, ListItemIcon, MenuItem, Tooltip } from '@mui/material';
import { AccountBalanceWallet, AddCircleOutline, Refresh } from '@mui/icons-material';
import { MRT_ColumnDef } from 'material-react-table';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TableView from '../../components/TableView';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { Util } from '../../util/Util';
import { NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from '../../util/constants';
import type { ClientWalletRow } from './walletTypes';
import { useWalletDirectory } from './useWalletDirectory';
import WalletClientModal from './WalletClientModal';
import WalletRecordModal from './WalletRecordModal';
import { canRecord } from '../../util/access';

const WalletList = () => {
  const { access } = useAuth();
  const { t } = useUI();
  const { wallets, lastUpdate, loading, fetchWallets, setWallets } = useWalletDirectory();
  const [selected, setSelected] = useState<ClientWalletRow | null>(null);
  const [postWallet, setPostWallet] = useState<ClientWalletRow | null>(null);
  const [postOpen, setPostOpen] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const canPost = canRecord(access, 'wallet');

  const refreshWallets = async () => {
    const res = await fetchWallets(true);
    if (res?.status === 200) {
      const rows = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setSelected((current) => {
        if (!current) return current;
        return rows.find((row: ClientWalletRow) => row.client_id === current.client_id) || current;
      });
    }
  };

  const columns: MRT_ColumnDef<ClientWalletRow>[] = useMemo(() => [
    {
      header: t('common:tables.client'),
      accessorKey: 'client',
      filterFn: 'contains',
      columnFilterModeOptions: TEXT_FILTER_OPTIONS,
      Cell: ({ cell, row }) => (
        <button
          type="button"
          style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => setSelected(row.original)}
        >
          {String(cell.getValue() ?? '')}
        </button>
      ),
    },
    {
      header: t('common:wallet.balance'),
      accessorKey: 'balance',
      Cell: ({ cell }) => Util.formatNumber(Number(cell.getValue() || 0)),
      filterFn: 'greaterThanOrEqualTo',
      columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
    },
    {
      header: t('common:ui.labels.status'),
      accessorKey: 'is_active',
      Cell: ({ cell }) => (
        <Chip
          size="small"
          color={cell.getValue() ? 'success' : 'default'}
          label={cell.getValue() ? t('common:wallet.active') : t('common:wallet.inactive')}
        />
      ),
    },
  ], [t]);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TableView
				resource="client_wallet"
          header={t('common:wallet.title')}
          data={wallets}
          columns={columns}
          lastUpdated={lastUpdate}
          useDateRangeFilter={false}
          isLoading={loading}
          totals={[{ label: t('common:wallet.balance'), key: 'balance' }]}
          renderTopToolbarActions={() => (
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
              <Tooltip title={t('common:ui.tooltips.refreshData')}>
                <IconButton onClick={() => void refreshWallets()} disabled={loading} color="primary">
                  <Refresh />
                </IconButton>
              </Tooltip>
              {canPost && (
                <Tooltip title={t('common:wallet.postMovement')}>
                  <IconButton
                    color="success"
                    onClick={() => {
                      setPostWallet(null);
                      setPostOpen(true);
                    }}
                  >
                    <AddCircleOutline />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          renderRowActionMenuItems={(row, closeMenu) => [
            <MenuItem
              key="open"
              onClick={() => {
                setSelected(row.original);
                closeMenu();
              }}
            >
              <ListItemIcon><AccountBalanceWallet fontSize="small" /></ListItemIcon>
              {t('common:wallet.viewRecords')}
            </MenuItem>,
            ...(canPost && row.original.is_active ? [
              <MenuItem
                key="post"
                onClick={() => {
                  setPostWallet(row.original);
                  setPostOpen(true);
                  closeMenu();
                }}
              >
                <ListItemIcon><AddCircleOutline fontSize="small" /></ListItemIcon>
                {t('common:wallet.postMovement')}
              </MenuItem>,
            ] : []),
          ]}
        />
      </LocalizationProvider>

      <WalletClientModal
        isOpen={!!selected}
        wallet={selected}
        onClose={() => setSelected(null)}
        onPost={() => {
          setPostWallet(selected);
          setPostOpen(true);
        }}
        onChanged={() => {
          void refreshWallets();
          setRefreshToken((n) => n + 1);
        }}
        refreshToken={refreshToken}
      />

      <WalletRecordModal
        isOpen={postOpen}
        wallet={postWallet}
        wallets={wallets}
        onWalletsChange={setWallets}
        onClose={() => setPostOpen(false)}
        onSuccess={() => {
          void refreshWallets();
          setRefreshToken((n) => n + 1);
        }}
      />
    </>
  );
};

export default WalletList;
